// import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import iaRequest from '@/iaRequest';
// import config from '@/config';
import { getEnv } from '@/utils/texts';
import { IInterviewQuestion, IWhyThisFlow } from '../Questions/query';
import { BotStatusType, ChatMessage } from '@/provider/InterviewProvider';
import { AxiosResponse } from 'axios';
import { showSuccess } from '@/components/AlertNotification';
import type { ConsentAuditTrail } from '@/core/protected/InterviewAnalysis/consentCoverage';

// ─── Tab type ────────────────────────────────────────────────────────────────

export type CandidateInfoType = 'OVERVIEW' | 'RESUME' | 'PLAN';
export const CandidateInfoTypeEnum = {
  OVERVIEW: 'OVERVIEW',
  RESUME: 'RESUME',
  PLAN: 'PLAN',
} as const;

// ─── Query Key Factory ────────────────────────────────────────────────────────

export const candidateKeys = {
  all: ['CANDIDATE_INFO'] as const,
  detail: (id: string) => [...candidateKeys.all, id] as const,
};

// ─── Types ────────────────────────────────────────────────────────────────────
interface QuestionPlan {
  id: string;
  category: string;
  estimatedTimeMinutes: number;
  question: string;
  idealCandidateAnswer: string;
  candidateAnswer?: string;
}

interface InterviewPlanType {
  plans: QuestionPlan[];
}
export interface ICandidateInfo {
  id: string;
  jobId: string;
  name: string;
  email: string;
  phone: string;
  location: string;
  interviewStatus: string;
  score: number;
  primarySkills: string[];
  softSkills: string[];
  appliedDate: string;
  resumeUrl: string;
  resumeName: string;
  jobDescUrl: string;
  role: string;
  interviewPlan?: InterviewPlanType;
  parsedData: {
    candidateName: string;
    firstName: string;
    middleName: string;
    lastName: string;
    candidateRole: string;
    region: string;
    regionFromPhoneNo: string;
    regionGroup: string;
    flagRegionMismatch: boolean;
    candidateAddress: string;
    address: string;
    phoneNo: string[];
    email: string[];
    githubUrl: string;
    yearsOfExperience: string;
    latestEmployer: string;
    degree: string[];
    technicalSkills: string[];
    softSkills: string[];
    certifications: {
      certificateTitle: string;
      certificateDetails: string[];
    }[];
    listedSkills: string[];
    workExperienceSkills: string[];
    projectsSkills: string[];
    workExperience: {
      company: string;
      role: string;
      seniority: string;
      responsibilities: string[];
      startDate: string;
      endDate: string;
      duration: string;
      technologyUsed: string[];
      domain: string;
    }[];
    projects: {
      projectTitle: string;
      projectDetails: string[];
      technologyUsed: string[];
      domain: string;
    }[];
    roles: string[];
    domains: string[];
    scoringResults: Record<string, unknown>;
    rawScoringResults: {
      hardSkills: {
        tag:
          | 'Explicit in Resume'
          | 'Explicitly Mentioned'
          | 'Inferred'
          | 'Gap'
          | 'Gap in Resume'
          | 'Not in Resume'
          | 'Not in JD';
        name: string;
        reasoning?: string;
        vote?: 'up' | 'down';
      }[];
      softSkills: {
        tag:
          | 'Explicit in Resume'
          | 'Explicitly Mentioned'
          | 'Inferred'
          | 'Gap'
          | 'Gap in Resume'
          | 'Not in Resume'
          | 'Not in JD';
        name: string;
        reasoning?: string;
        vote?: 'up' | 'down';
      }[];
      topFindings: {
        tag: 'Explicitly Mentioned in Resume' | 'Inferred from Resume';
        text: string;
      }[];
      experienceMatch: {
        score: number;
        description: string;
        candidateData: string;
        jdRequirement: string;
        industryDomain: string;
        candidateDataYears: number;
        careerPivotInsight: string;
        jdRequirementYears: number;
        sameJobTitleInsight: string;
        candidateDataRelevantYears: number;
      };
      roleSuitabilityScore: {
        score: number;
        summary: string;
      };
    };
  };
}
export interface InterviewPlanItem {
  id: string | number;
  category?: string;
  label?: string;
  estimatedTimeMinutes?: number;
  time?: number;
  question: string;
  idealCandidateAnswer?: string;
  expected_answer_context?: string;
  shortened_question?: string;
  skills_to_be_assessed?: string[];
  why_this_flow?: IWhyThisFlow[];
  initial_question_id?: number;
}

// const MOCK_BASE_URL = 'http://localhost:3001';

interface ICandidatesInfoRaw {
  id: string;
  jobId: string;
  parsedData: ICandidateInfo['parsedData'];
  resumeUrl: string;
  resumeName: string;
}

const getCandidateDetails = (candidateId: string): Promise<ICandidateInfo | null> =>
  iaRequest
    .get<ICandidatesInfoRaw | null>(`/parser/candidate/${candidateId}`, {
      baseURL: getEnv('VITE_RESUME_PARSER_BASE_URL'),
    })
    .then(res => {
      if (!res.data) return null;
      const { id, jobId, parsedData, resumeUrl, resumeName } = res.data;
      return {
        id,
        jobId,
        name: parsedData.candidateName,
        email: parsedData.email[0] ?? '',
        phone: parsedData.phoneNo[0] ?? 'N/A',
        location: parsedData.candidateAddress ?? parsedData.region ?? 'N/A',
        interviewStatus: 'Scheduled',
        score: 0,
        primarySkills: parsedData.technicalSkills,
        softSkills: parsedData.softSkills,
        appliedDate: '',
        role: parsedData.candidateRole,
        resumeUrl,
        resumeName,
        jobDescUrl: '',
        parsedData,
      };
    });

export const useGetCandidateDetails = (id: string) =>
  useQuery({
    queryKey: candidateKeys.detail(id),
    queryFn: () => getCandidateDetails(id),
    enabled: !!id,
  });

// ─── Interview Details Types ──────────────────────────────────────────────────

export interface IEventDetails {
  event_date: string | null;
  start_time: string | null;
  end_time: string | null;
  duration: number;
  participants: string[];
}

export const InterviewStatus = {
  COMPLETED: 'Completed',
  SCHEDULED: 'Scheduled',
  NOT_SCHEDULED: 'Not Scheduled',
  IN_PROGRESS: 'In Progress',
  OVERDUE: 'Overdue',
  CANCELLED: 'Cancelled',
  NEEDS_REVIEW: 'Needs Review',
} as const;

export type InterviewStatusType = keyof typeof InterviewStatus;

/**
 * One of the campaign's standard questions as it reaches a candidate's plan.
 *
 * Copied onto the interview when it was scheduled, so it carries its own id and
 * is read-only here — the list itself is edited on the campaign.
 */
export interface IInterviewStandardQuestion {
  id: number;
  question: string;
  /** Its place in the campaign's list, counting from 1. The live socket counts
   *  the same ordering from 0 — the question at `position` 3 is asked as
   *  `question_index` 2 — so matching an asked question back to its row means
   *  comparing `position - 1`. See LiveInterview/Questions/StandardQuestions. */
  position: number;
}

export interface IInterviewDetails {
  id: number;
  meet_link: string;
  interviewee_name: null;
  interview_duration: number;
  candidate_id: string;
  interview_status: InterviewStatusType;
  questions: IInterviewQuestion[];
  event_details: null;
  bot_status: BotStatusType;
  time_constraint_warning: string;
  why_this_flow_summary: string;
  interview_logs: ChatMessage[];
  interview_scheduled_time: string;
  interview_timezone: string;
  provider: 'google' | 'zoom' | 'microsoft';
  meet_link_source: 'CALENDAR' | 'MANUAL';
  candidate_email: string;
  calendar_event_name: string | null;
  /** Absent on interviews scheduled before the campaign had any. */
  standard_questions?: IInterviewStandardQuestion[];
}

// ─── Interview Details Query Key Factory ──────────────────────────────────────

export const interviewDetailKeys = {
  all: ['INTERVIEW_DETAILS'] as const,
  byCandidateId: (candidateId: string) => [...interviewDetailKeys.all, candidateId] as const,
};

// ─── Interview Details API + Hook ─────────────────────────────────────────────

const getInterviewDetails = (candidateId: string): Promise<IInterviewDetails> =>
  iaRequest.get<IInterviewDetails>(`/interview-details/candidate/${candidateId}`).then(res => res.data);

export const useGetInterviewDetails = (candidateId: string) =>
  useQuery({
    queryKey: interviewDetailKeys.byCandidateId(candidateId),
    queryFn: () => getInterviewDetails(candidateId),
    enabled: !!candidateId,
  });

const putStandardQuestionOrder = (candidateId: string, standardQuestionIds: number[]): Promise<IInterviewDetails> =>
  iaRequest
    .put<IInterviewDetails>(`/interview-details/candidate/${candidateId}/standard-questions/order`, {
      standard_question_ids: standardQuestionIds,
    })
    .then(res => res.data);

const orderByIds = (questions: IInterviewStandardQuestion[] | undefined, ids: number[]) => {
  const byId = new Map((questions ?? []).map(question => [question.id, question]));
  return ids.flatMap(id => byId.get(id) ?? []);
};

/**
 * Moves one of the campaign's standard questions on a candidate's plan.
 *
 * Each move sends the full order and applies it to the cache straight away.
 * Moves are serialised in one scope so the server sees them in the order they
 * were made, and only the last one still in flight may write to the cache on
 * settling — an earlier response or rollback would undo the moves after it.
 */
export const useReorderStandardQuestions = (candidateId: string) => {
  const queryClient = useQueryClient();
  const queryKey = interviewDetailKeys.byCandidateId(candidateId);
  const mutationKey = ['REORDER_STANDARD_QUESTIONS', candidateId];
  // Counts this call too: callbacks run while it is still pending.
  const isLatest = () => queryClient.isMutating({ mutationKey }) === 1;

  const getStandardQuestions = () => queryClient.getQueryData<IInterviewDetails>(queryKey)?.standard_questions;
  const setStandardQuestions = (questions: IInterviewStandardQuestion[] | undefined) =>
    queryClient.setQueryData<IInterviewDetails>(queryKey, old => old && { ...old, standard_questions: questions });

  const { mutate } = useMutation({
    mutationKey,
    scope: { id: `REORDER_STANDARD_QUESTIONS:${candidateId}` },
    mutationFn: (standardQuestionIds: number[]) => putStandardQuestionOrder(candidateId, standardQuestionIds),
    // Synchronous on purpose: the next keyboard move reads this order from the cache.
    // A manual setQueryData also becomes the cancelled fetch's revert state, so the
    // cancel cannot restore the pre-move order over it.
    onMutate: standardQuestionIds => {
      const previous = getStandardQuestions();
      void queryClient.cancelQueries({ queryKey });
      setStandardQuestions(orderByIds(previous, standardQuestionIds));
      return { previous };
    },
    onSuccess: data => {
      if (isLatest()) setStandardQuestions(data.standard_questions);
    },
    onError: (_error, _ids, context) => {
      if (!isLatest()) return;
      setStandardQuestions(context?.previous);
      // The snapshot may itself be an earlier optimistic order that also failed.
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Reads the cache, not render props: a run of key presses can land before the re-render.
  return (from: number, to: number) => {
    const ids = (getStandardQuestions() ?? []).map(question => question.id);
    if (from === to || from < 0 || to < 0 || from >= ids.length || to >= ids.length) return;
    const [moved] = ids.splice(from, 1);
    ids.splice(to, 0, moved);
    mutate(ids);
  };
};

export const useCancelInterview = () => {
  return useMutation({
    mutationKey: ['CANCEL_INTERVIEW'],
    mutationFn: async ({ interviewId, cancelled_reason }: { interviewId: string; cancelled_reason: string }) => {
      const response: AxiosResponse<{ message: string }> = await iaRequest.patch(
        `calendar-sync/interview/${interviewId}`,
        {
          cancelled_reason,
        },
      );
      return response.data;
    },
    onSuccess: _data => {
      showSuccess('Interview cancelled successfully');
    },
  });
};

/** Matches `PHASE_POLL_MS` on the candidate's own consent page. */
const CONSENT_STATUS_POLL_MS = 15_000;

// ─── Consent link (recruiter copyable link ) ─────────────────────
// Lazy + idempotent: fetching mints the token if absent and returns the same URL thereafter.

export interface IConsentLink {
  token: string;
  expires_at: string;
}

/**
 * This interview's consent link, or `null` when none has been minted.
 *
 * A read, and only a read. This used to mint on GET, and because both this page and the
 * live-interview header call it on every load, a credential that opens a meeting came into being
 * for interviews at companies with consent switched off, for Zoom interviews that cannot carry a
 * consent page, and for interviews nobody had scheduled. Only scheduling mints one now.
 */
export const useGetConsentLink = (interviewId?: number) =>
  useQuery<IConsentLink | null>({
    queryKey: ['CONSENT_LINK', interviewId],
    queryFn: () =>
      iaRequest.get<IConsentLink | null>(`/interview-detail/${interviewId}/consent-link`).then(res => res.data),
    enabled: !!interviewId,
    staleTime: 5 * 60 * 1000,
  });

/**
 * This interview's consent decisions, oldest first — the audit trail behind the report banner.
 *
 * A trail, not a status: the report has to say what was *not* covered, and a withdrawal that was
 * later reversed leaves a gap that the latest decision alone cannot describe.
 */
export const useGetConsentRecords = (interviewId?: number, { poll = false }: { poll?: boolean } = {}) =>
  useQuery<ConsentAuditTrail>({
    queryKey: ['CONSENT_RECORDS', interviewId],
    queryFn: () =>
      iaRequest
        .get<ConsentAuditTrail>(`/interview-detail/${interviewId}/consent-records`)
        .then(res => res.data ?? { obligated: null, records: [] }),
    enabled: !!interviewId,
    // Polled only where someone is watching for an answer to land; the report reads it once.
    staleTime: poll ? 5 * 1000 : 5 * 60 * 1000,
    refetchOnWindowFocus: poll,
    refetchInterval: poll ? CONSENT_STATUS_POLL_MS : false,
  });

/**
 * Whether consent is part of this interview's story at all — and so whether the page should carry
 * any consent UI.
 *
 * False only for an interview that was explicitly never obliged to ask *and* never got an answer.
 * Both halves matter. `obligated` is tri-state and `null` means nothing was ever pinned, which is
 * not a decision that consent was unnecessary; and a recruiter may mint a link by hand for an
 * interview the company did not require one for, in which case the candidate's answer is real and
 * the link is theirs to see.
 *
 * Returns false until the trail has loaded, so the row appears once rather than appearing and
 * then vanishing.
 */
export const useConsentApplies = (interviewId?: number): boolean => {
  const { data: trail } = useGetConsentRecords(interviewId);
  if (!trail) return false;
  return trail.obligated !== false || trail.records.length > 0;
};

export const useTakeAction = () => {
  return useMutation({
    mutationKey: ['TAKE_ACTION'],
    mutationFn: async ({ interviewId, completed }: { interviewId: string; completed: boolean }) => {
      const response: AxiosResponse<{ message: string }> = await iaRequest.patch(`interview-status/${interviewId}`, {
        completed,
      });
      return response.data;
    },
    onSuccess: _data => {
      showSuccess('Interview status changed successfully');
    },
  });
};
